import { useState, useCallback, useMemo, useRef } from 'react';
import { SupportedLanguages } from '@/i18n/i18next';
import { WizardService } from '../services/wizard.service';
import {
  WizardFlow,
  WizardStep,
  WizardState,
  WizardNavigationHistory,
} from '../types/wizard.types';
import { InteractionFormData } from '@momentum/types';

export interface UseWizardReturn<
  TFormData extends InteractionFormData = InteractionFormData
> {
  // Current state
  currentStep: WizardStep<TFormData> | null;
  state: WizardState;
  isCompleted: boolean;
  progress: number;
  canGoBack: boolean;

  // Actions
  startFlow: () => void;
  updateStep: (answer: any) => void;
  answerStep: (answer: any) => void;
  goToPreviousStep: () => void;
  goToStep: (stepId: string) => void;
  setLanguage: (language: SupportedLanguages) => void;
  reset: () => void;

  // Utilities
  getStepById: (stepId: string) => WizardStep | null;
  validateCurrentStep: (answer: any) => string | null;
  getNavigationHistory: () => WizardNavigationHistory[];
  getFlowInfo: () => { id: string; title?: string; description?: string };
}

export const useWizard = (
  flow: WizardFlow<InteractionFormData>,
  initialLanguage: SupportedLanguages = 'en',
  initalState?: Partial<WizardState>,
  firstStepId?: string
): UseWizardReturn => {
  // State for triggering re-renders
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => forceUpdate({}), []);

  const initialStateRef = useRef(initalState);

  // Create service instance (recreate when flow changes)
  const service = useMemo(() => {
    console.log('🔄 useWizard: Creating new service for flow:', flow.id);
    return new WizardService(
      flow,
      initialLanguage,
      initialStateRef.current,
      firstStepId
    );
  }, [flow.id, flow.firstStepId, initialLanguage, firstStepId]);

  // Current step
  const currentStep = service.getCurrentStep();

  // Current state
  const state = service.getState();

  // Flow status
  const isCompleted = service.isCompleted();
  const progress = service.getProgress();
  const canGoBack = service.canGoBack();

  // Debug logging
  console.log('🎯 useWizard: Render cycle - currentStep:', currentStep?.id);
  console.log('🎯 useWizard: Render cycle - state:', state);
  console.log('🎯 useWizard: Render cycle - isCompleted:', isCompleted);

  // Actions
  const startFlow = useCallback(() => {
    console.log('🚀 useWizard: startFlow called');
    const result = service.startFlow();
    console.log('🚀 useWizard: startFlow result:', result?.id);
    triggerUpdate();
  }, [service, triggerUpdate]);

  const updateStep = useCallback(
    (answer: any) => {
      console.log('📝 useWizard: updateStep called with:', answer);
      const result = service.updateStep(answer);
      console.log(
        '📝 useWizard: updateStep result:',
        result?.id || 'completed'
      );
    },
    [service]
  );

  const answerStep = useCallback(
    (answer: any) => {
      console.log('📝 useWizard: answerStep called with:', answer);
      const result = service.answerStep(answer);
      console.log(
        '📝 useWizard: answerStep result:',
        result?.id || 'completed'
      );
      triggerUpdate();
    },
    [service, triggerUpdate]
  );

  const goToPreviousStep = useCallback(() => {
    console.log('⬅️ useWizard: goToPreviousStep called');
    const result = service.goToPreviousStep();
    console.log('⬅️ useWizard: goToPreviousStep result:', result?.id);
    triggerUpdate();
  }, [service, triggerUpdate]);

  const goToStep = useCallback(
    (stepId: string) => {
      console.log('🎯 useWizard: goToStep called with:', stepId);
      const result = service.goToStep(stepId);
      console.log('🎯 useWizard: goToStep result:', result?.id);
      triggerUpdate();
    },
    [service, triggerUpdate]
  );

  const setLanguage = useCallback(
    (language: SupportedLanguages) => {
      console.log('🌐 useWizard: setLanguage called with:', language);
      service.setLanguage(language);
      triggerUpdate();
    },
    [service, triggerUpdate]
  );

  const reset = useCallback(() => {
    console.log('🔄 useWizard: reset called');
    service.reset();
    console.log('🔄 useWizard: reset completed');
    triggerUpdate();
  }, [service, triggerUpdate]);

  // Utilities
  const getStepById = useCallback(
    (stepId: string) => {
      return service.getStepById(stepId);
    },
    [service]
  );

  const validateCurrentStep = useCallback(
    (answer: any) => {
      return service.validateCurrentStep(answer);
    },
    [service]
  );

  const getNavigationHistory = useCallback(() => {
    return service.getNavigationHistory();
  }, [service]);

  const getFlowInfo = useCallback(() => {
    return service.getFlowInfo();
  }, [service]);

  return {
    // Current state
    currentStep,
    state,
    isCompleted,
    progress,
    canGoBack,

    // Actions
    startFlow,
    updateStep,
    answerStep,
    goToPreviousStep,
    goToStep,
    setLanguage,
    reset,

    // Utilities
    getStepById,
    validateCurrentStep,
    getNavigationHistory,
    getFlowInfo,
  };
};
